<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useMutation } from '@tanstack/vue-query';
import {
  requirementsApi, REQUIREMENT_FREQUENCIES, type Requirement, type RequirementForm,
} from '@/api/planning/requirements';
import { useCareerSkills } from '@/composables/useCareerSkills';
import { ALL_CAREERS, careerLabel, careerMetadata } from '@/utils/career-metadata';
import { FREQUENCY_LABELS_ES, PERIOD_NOUNS_ES } from '@/utils/monthly-expectation';
import { usePermissions } from '@/composables/usePermissions';

const DEFAULT_DURATION_MIN = 60;
const DEFAULT_VISITS_PER_PERIOD = 3;
const ISO_DATE_LENGTH = 10;

interface Props {
  modelValue: boolean;
  patientId: number;
  requirement?: Requirement | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
  (e: 'success'): void;
}>();

const { hasPermission } = usePermissions();

const careerOptions = computed(() => ALL_CAREERS
  .filter((value) => hasPermission.value('careers', `read_${value}`))
  .map((value) => {
    const meta = careerMetadata(value);
    return { value, title: careerLabel(value), icon: meta.icon, color: meta.color };
  }));

const validationSchema = yup.object({
  career: yup.string().required('Selecciona una especialidad'),
  frequency: yup.string()
    .oneOf(REQUIREMENT_FREQUENCIES, 'Selecciona una frecuencia')
    .required('Selecciona una frecuencia'),
  visitsPerPeriod: yup.number()
    .typeError('Ingresa un número válido')
    .required('Requerido')
    .integer('Debe ser un número entero')
    .min(1, 'Debe ser mayor a 0'),
  durationMin: yup.number()
    .typeError('Ingresa un número válido')
    .required('Requerido')
    .integer('Debe ser un número entero')
    .min(1, 'Debe ser mayor a 0'),
  startsOn: yup.string().required('Selecciona una fecha de inicio'),
  endsOn: yup.string().nullable().test(
    'ends-after-starts',
    'Debe ser posterior o igual a la fecha de inicio',
    function endsAfterStarts(value) {
      if (!value) return true;
      const { startsOn } = this.parent;
      if (!startsOn) return true;
      return value >= startsOn;
    },
  ),
});

function defaultValues(): RequirementForm {
  return {
    career: '',
    skillId: null,
    frequency: 'weekly',
    visitsPerPeriod: DEFAULT_VISITS_PER_PERIOD,
    durationMin: DEFAULT_DURATION_MIN,
    startsOn: new Date().toISOString().slice(0, ISO_DATE_LENGTH),
    endsOn: null,
  };
}

function fromRequirement(req: Requirement): RequirementForm {
  return {
    career: req.career,
    skillId: req.skillId,
    frequency: req.frequency,
    visitsPerPeriod: req.visitsPerPeriod,
    durationMin: req.durationMin,
    startsOn: req.startsOn,
    endsOn: req.endsOn || null,
  };
}

const {
  handleSubmit, values, errors, defineField, resetForm,
} = useForm<RequirementForm>({
  validationSchema,
  initialValues: defaultValues(),
});

const [career, careerAttrs] = defineField('career');
const [skillId, skillIdAttrs] = defineField('skillId');
const [frequency, frequencyAttrs] = defineField('frequency');
const [visitsPerPeriod, visitsPerPeriodAttrs] = defineField('visitsPerPeriod');
const [durationMin, durationMinAttrs] = defineField('durationMin');
const [startsOn, startsOnAttrs] = defineField('startsOn');
const [endsOn, endsOnAttrs] = defineField('endsOn');

const errorMessage = ref<string>('');

const { careerSkills } = useCareerSkills(career, skillId, null);

watch(() => props.modelValue, (open) => {
  if (!open) return;
  errorMessage.value = '';
  resetForm({
    values: props.requirement ? fromRequirement(props.requirement) : defaultValues(),
  });
}, { immediate: true });

const { mutate: save, isPending } = useMutation({
  mutationFn: (form: RequirementForm) => (
    props.requirement ?
      requirementsApi.update(props.requirement.id, form) :
      requirementsApi.create(props.patientId, form)
  ),
  onSuccess: () => {
    emit('success');
    emit('update:modelValue', false);
  },
  onError: (error: { response?: { data?: { errors?: string[] } } }) => {
    errorMessage.value = error?.response?.data?.errors?.join(', ') || 'Error al guardar';
  },
});

const onSubmit = handleSubmit((formValues) => {
  save({
    ...formValues,
    skillId: formValues.skillId || null,
    visitsPerPeriod: Number(formValues.visitsPerPeriod),
    durationMin: Number(formValues.durationMin),
    endsOn: formValues.endsOn || null,
  });
});

function close() {
  emit('update:modelValue', false);
}

const careerMeta = computed(() => (values.career ? careerMetadata(values.career) : null));

const frequencyOptions = REQUIREMENT_FREQUENCIES.map(
  (value) => ({ value, title: FREQUENCY_LABELS_ES[value] }),
);

// The backend refuses to change the frequency while weekly plans exist, so the select
// stays locked until they are removed.
const frequencyLocked = computed(() => (props.requirement?.weeklyPlans.length ?? 0) > 0);

const schedulesManually = computed(() => values.frequency !== 'weekly');

const periodNoun = computed(() => PERIOD_NOUNS_ES[values.frequency] ?? PERIOD_NOUNS_ES.weekly);

const frequencyHint = computed(() => {
  if (frequencyLocked.value) return 'Elimina los planes semanales para cambiar la frecuencia';
  if (schedulesManually.value) {
    return 'Sin plan semanal: los turnos se agendan manualmente desde el calendario';
  }
  return '';
});

const totalMinutesPerPeriod = computed(
  () => Number(values.visitsPerPeriod || 0) * Number(values.durationMin || 0),
);

const isFormReady = computed(() => (
  !!values.career && Number(values.visitsPerPeriod) >= 1 &&
  Number(values.durationMin) >= 1 && !!values.startsOn
));
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="640"
    persistent
    @update:model-value="close"
  >
    <v-form @submit.prevent="onSubmit">
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center ga-3 pa-4 pb-2">
          <v-icon
            icon="mdi-clipboard-list-outline"
            color="primary"
          />
          <div>
            <div class="text-h6 font-weight-bold">
              {{ requirement ? 'Editar requerimiento' : 'Nuevo requerimiento' }}
            </div>
            <div class="text-caption text-medium-emphasis">
              Define la frecuencia y duración de los turnos acordados
            </div>
          </div>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-5">
          <v-alert
            v-if="errorMessage"
            type="error"
            variant="tonal"
            closable
            class="mb-4"
            @click:close="errorMessage = ''"
          >
            {{ errorMessage }}
          </v-alert>

          <v-row dense>
            <v-col cols="12">
              <v-select
                v-model="career"
                v-bind="careerAttrs"
                :items="careerOptions"
                item-title="title"
                item-value="value"
                label="Especialidad"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-stethoscope"
                :error-messages="errors.career"
                required
              >
                <template #selection="{ item }">
                  <v-icon
                    :icon="item.raw.icon"
                    :color="item.raw.color"
                    size="small"
                    class="mr-2"
                  />
                  {{ item.raw.title }}
                </template>
                <template #item="{ item, props: itemProps }">
                  <v-list-item v-bind="itemProps">
                    <template #prepend>
                      <v-icon
                        :icon="item.raw.icon"
                        :color="item.raw.color"
                        size="small"
                      />
                    </template>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>

            <v-col
              v-if="career && careerSkills && careerSkills.length > 0"
              cols="12"
            >
              <v-select
                v-model="skillId"
                v-bind="skillIdAttrs"
                :items="careerSkills"
                item-title="name"
                item-value="id"
                label="Subespecialidad (opcional)"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-certificate-outline"
                clearable
                hint="Sólo profesionales con esta subespecialidad podrán ser asignados"
                persistent-hint
                class="mb-3"
              />
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="frequency"
                v-bind="frequencyAttrs"
                :items="frequencyOptions"
                item-title="title"
                item-value="value"
                label="Frecuencia"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-refresh"
                :disabled="frequencyLocked"
                :hint="frequencyHint"
                :persistent-hint="!!frequencyHint"
                :error-messages="errors.frequency"
                required
              />
            </v-col>

            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="visitsPerPeriod"
                v-bind="visitsPerPeriodAttrs"
                :label="`Turnos por ${periodNoun}`"
                type="number"
                min="1"
                variant="outlined"
                density="comfortable"
                :prepend-inner-icon="schedulesManually ? 'mdi-calendar-month' : 'mdi-calendar-week'"
                :error-messages="errors.visitsPerPeriod"
                required
              />
            </v-col>

            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="durationMin"
                v-bind="durationMinAttrs"
                label="Duración (min)"
                type="number"
                min="1"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-timer-outline"
                suffix="min"
                :error-messages="errors.durationMin"
                required
              />
            </v-col>

            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="startsOn"
                v-bind="startsOnAttrs"
                label="Desde"
                type="date"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-start"
                :error-messages="errors.startsOn"
                required
              />
            </v-col>

            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="endsOn"
                v-bind="endsOnAttrs"
                label="Hasta (opcional)"
                type="date"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-end"
                :error-messages="errors.endsOn"
                clearable
              />
            </v-col>
          </v-row>

          <v-expand-transition>
            <v-card
              v-if="isFormReady && careerMeta"
              variant="tonal"
              color="primary"
              class="mt-3"
            >
              <v-card-text class="d-flex align-center ga-3 py-3">
                <v-icon
                  :icon="careerMeta.icon"
                  :color="careerMeta.color"
                  size="large"
                />
                <div class="flex-grow-1">
                  <div class="text-subtitle-2 font-weight-bold">
                    Resumen del requerimiento
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ values.visitsPerPeriod }} turnos/{{ periodNoun }}
                    · {{ values.durationMin }} min por turno
                    · {{ totalMinutesPerPeriod }} min/{{ periodNoun }} totales
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-expand-transition>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            text="Cancelar"
            variant="text"
            @click="close"
          />
          <v-btn
            type="submit"
            color="primary"
            variant="flat"
            prepend-icon="mdi-check"
            text="Guardar"
            :loading="isPending"
          />
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>
